import { Injectable } from "@angular/core";
import { Color } from "@lib/color";
import { DateTime } from "@lib/date-time";
import { BehaviorSubject, Observable, OverrideSubject, Subject, combineLatest, concat, debounceTime, defer, firstValueFrom, generate, interval, map, of, op, switchMap } from "@lib/rxjs";
import { storageLocal } from "@lib/storage-local";
import { WebClient } from "./web-client";
import { DataIntervalTagEditDialog } from "@app/shared/edit-dialog/data-interval-tag/data-interval-tag-edit-dialog.component";
import { DataIntervalTag, Device, Shift, User, fragQuery, muckpileQuery, RemoveDataInterval, TimelineCustomSizes, TaggingMuckpile, MuckpileStatusUpdate } from "@models";
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { percentageDefaultColors, percentageNewColors } from "src/colors";
import { User$ } from "./login.service";
import { chart } from "@lib/angular/chart";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MeasurementTypeToUnit } from "@lib/angular/measurement/measurement-type-to-unit";
import { UnitSystem } from "@lib/measurement/types";
import { measurement } from "@lib/measurement";
import { toZonedTime } from 'date-fns-tz';
import { getUserIANATimezone } from "@lib/timezone-preference-list";
import { take, filter, startWith, shareReplay, finalize } from 'rxjs/operators'; 
import { asFiniteNumber } from "@lib/number";
import { snapshotManager } from "@app/utilities/snapshot/snapshot-manager";
import { getSummaryData } from "@app/new-ui/analysis/analysis.component";
import { Node } from "@models";
import { FoblexNode } from "@app/utilities/foblex-flow/base-node/base-node";
import { TimeRange } from "@app/utilities/time";

interface DurationOption {
    label: string;
    type: DateTime.Step | 'custom' | 'shifts' | 'week';
    key?: string;
}

const durationOptions: DurationOption[] = [
    {
        label: 'Last Hour',
        type: [1, 'hour'],
    },
    {
        label: 'Last 3 Hours',
        type: [3, 'hour'],
    },
    {
        label: 'Last 6 Hours',
        type: [6, 'hour'],
    },
    {
        label: 'Last 12 Hours',
        type: [12, 'hour'],
    },
    {
        label: 'Last 24 Hours',
        type: [1, 'day'],
    },
    {
        label: 'Week',
        type: 'week',
    },
    {
        label: 'Custom',
        type: 'custom',
    },
    {
        label: 'Shifts',
        type: 'shifts'
    }
];
for (const opt of durationOptions)
    opt.key = Array.isArray(opt.type) ? opt.type.join('') : opt.type;
const durationOptionMap = new Map(durationOptions.map((v) => [v.key, v]));
const maxTimelineSpanMonths = 1;

function liveTimeRange(step:DateTime.Step){
	return interval(5*60*1000)
		.pipe(
			op.startWith(-1),
			op.map(()=>{
				const end=new DateTime();
				const begin=end.clone().sub(step);
				return {
					begin,
					end,
				};
			}));
}

@Injectable({
    providedIn: "root",
})
export class FragQueryService {
    constructor(
        private readonly dialog: DialogService,
        private readonly webClient: WebClient,
        private readonly user: User,
        private readonly user$: User$,
        private snackBar: MatSnackBar,
        private readonly measurementTypeToUnit: MeasurementTypeToUnit
    ) {
        // Persist siteId to local Storage whenever it changes:
        this.siteId$.subscribe(id => {
            if (id == null) {
                storageLocal.delete('frag-query-site-id');
            } else {
                storageLocal.set('frag-query-site-id', id);
            }
        });
        this.init();
        this.registerUnloadListener();
    }

    private unitAbbreviations: { [key: string]: string } = {
        'millimeter': 'mm',
        'centimeter': 'cm',
        'meter': 'm',
        'inch': 'in'
    };

    private unitSystem: { [key: string]: UnitSystem } = {
        'millimeter': 'smallMetricUnit',
        'centimeter': 'mediumMetricUnit',
        'meter': 'largeMetricUnit',
        'inch': 'imperialUnit',
    }

    private QuantitySystems: { [key: string]: UnitSystem } = {
        'meter': 'metricUnit',
        'yard': 'imperialUnit',
    }
    private lastTimelineSpanLimitSnackbarKey: string | null = null;

    private registerUnloadListener() {
        window.addEventListener('beforeunload', this.clearLocalStorage);
    }

    private clearLocalStorage() {
        localStorage.removeItem("frag-camera-frag-query-duration");
        localStorage.removeItem("frag-camera-frag-query-begin");
        localStorage.removeItem("frag-camera-frag-query-end");
    }

    private exceedsCalendarMonthLimit(timeRange: { begin: DateTime; end: DateTime }): boolean {
        const maxEnd = new DateTime(timeRange.begin).add([maxTimelineSpanMonths, 'month']);
        return new DateTime(timeRange.end).getTime() > maxEnd.getTime();
    }

    private isTimelineSpanTooLong(timeRange: { begin: DateTime; end: DateTime }): boolean {
        return this.exceedsCalendarMonthLimit(timeRange);
    }

    private showTimelineSpanLimitSnackbar(timeRange: { begin: DateTime; end: DateTime }): void {
        const snackbarKey = `${timeRange.begin.valueOf()}:${timeRange.end.valueOf()}`;
        if (this.lastTimelineSpanLimitSnackbarKey === snackbarKey) {
            return;
        }

        this.lastTimelineSpanLimitSnackbarKey = snackbarKey;
        this.snackBar.open(
            `Please select a duration of up to ${maxTimelineSpanMonths} month.`,
            'OK',
            {
                duration: 5000,
                panelClass: ['snackbar-success']
            },
        );
    }

    private liveTimeRange(step: DateTime.Step) {
        return interval(5 * 60 * 1000)
            .pipe(
                op.startWith(-1),
                op.map(() => {
                    const end = new DateTime();
                    this.end$.next(end)
                    const begin = end.clone().sub(step);
                    this.begin$.next(begin)
                    return {
                        begin,
                        end,
                    };
                }));
    }

    private getInitialSiteId(): number | null {
        return asFiniteNumber(storageLocal.get('frag-query-site-id'));
    }

    //==================== analysis page data variables ==============//
    public trigger$ = new BehaviorSubject<void>(null);
    public readonly userSettingUpdated$ = new Subject<void>();
    public readonly siteId$: BehaviorSubject<number|null> = new BehaviorSubject<number|null>(this.getInitialSiteId());

    public readonly sites$ = combineLatest([ this.trigger$, this.user$ ]).pipe(
        switchMap(([_, user]) => {
            return this.webClient.model.site.search$({ sort: { name: '+' } });
        }),
    );

    // Modify devices to depend also on siteIds, and filtering accordingly
    public readonly devices$ = combineLatest([this.trigger$, this.siteId$, this.user$]).pipe(
        switchMap(([_, selectedSiteId, user]) => {
            let where: any = {};
            if (selectedSiteId != null) {
                // site explicitly selected: filter by that site
                where.siteId = selectedSiteId;
            } else {
                // no site selected: fallback by permission
                if (user.permissions >= 4) {
                    // SuperAdmin: no extra where (all accessible)
                    where = {};
                }
                else if (user.permissions >= 3) {
                    // MultiClientAdmin: all devices under their multi client
                    if (Array.isArray(user.clientIds) && user.clientIds.length > 0) {
                        where['clientId'] = [['in', user.clientIds]];
                    }
                }
                else if (user.permissions >= 2) {
                    // ClientAdmin: all devices under their client
                    if (Array.isArray(user.clientIds) && user.clientIds.length > 0) {
                        where['clientId'] = [['in', user.clientIds]];
                    }
                } else {
                    // SiteAdmin or Standard: only devices under their site
                    where.siteId = user.siteId;
                }
            }
            const sort: any = { clientId: '+', id: '+' };
            return this.webClient.model.device.search$({ where, sort });
        })  
    );

    private localPositions = new Map<number, FoblexNode.Position>();
    public readonly nodesLoading$ = new BehaviorSubject<boolean>(false);
    public nodeTrigger$ = new BehaviorSubject<void>(null);

    public readonly nodes$ = combineLatest([this.trigger$, this.siteId$, this.user$, this.nodeTrigger$])
    .pipe(
        switchMap(([_, selectedSiteId, user]) => {
            const where: any = selectedSiteId != null ? { siteId: selectedSiteId } : {};
            const sort: any = { id: '+' };
            this.nodesLoading$.next(true);
            return this.webClient.model.node.search$({ where, sort }).pipe(
                finalize(() => this.nodesLoading$.next(false)),
                startWith<Node[]>([]),
            );

        }),
        op.map(nodes => {
            const baseNodes = nodes.map(node => {
                 const cachedPos = this.localPositions.get(node.id);
                if (cachedPos) node.position = JSON.stringify(cachedPos);
               return node.toBaseNode(node)});
            return baseNodes;
        }),
        shareReplay(1)
    );

    public cacheNodePosition(id: number, pos: FoblexNode.Position) {
        this.localPositions.set(id, pos);
    }
    
    public clearCachedPositions() {
        this.localPositions.clear();
    }

    public readonly nodeConnections$ = combineLatest([this.trigger$, this.siteId$, this.user$]).pipe(
        switchMap(([_, selectedSiteId, user]) => {
            let where: any = {};
            if (selectedSiteId != null) {
                where.siteId = selectedSiteId;
            }
            const sort: any = { id: '+' };
            return this.webClient.model.nodeConnection.search$({ where, sort }).pipe(startWith(undefined));
        }),
        shareReplay(1)
    );

    public readonly devicesForNodeCreation$ = combineLatest([
        this.trigger$,
        this.devices$,
        this.nodes$,
    ]).pipe(
        switchMap(([_, devices,nodes]) => {
            const nodeDeviceIds = nodes.map(n => n.resourceId);
            const availableDevices = devices.filter(device => !nodeDeviceIds.includes(device.id));
            return of(availableDevices); 
        }),
        op.shareReplay(1)
    );

    public readonly defaultAddNodeDeviceUuid$ = this.devicesForNodeCreation$.pipe(
        op.map((v) => v?.[0]?.uuid ?? "")
    );

    public readonly selectedDeviceUuidForNode$: OverrideSubject<string> = new OverrideSubject(this.defaultAddNodeDeviceUuid$);
    public readonly selectedDeviceForNode$ = combineLatest([
        this.devicesForNodeCreation$,
        this.selectedDeviceUuidForNode$
    ]).pipe(
        op.map(([devices, uuid]) => devices.find(d => d.uuid === uuid) ?? null),
        op.shareReplay(1)
    );
  

    public readonly muckpiles$ = combineLatest([this.trigger$, this.siteId$]).pipe(
        op.switchMap(([_, siteId]) => {
            const selectedSiteId = asFiniteNumber(siteId || 0);
            const body: muckpileQuery.list.Body = {
                email: this.user.email,
                authToken: this.user.authToken,
                siteId: selectedSiteId || undefined,
            };
            return this.webClient
                .rest$({
                    route: '/muckpile-query/list',
                    method: 'get',
                    body,
                })
                .pipe(op.startWith(undefined));
        }),
        op.traverse((r) => r.toRecord<muckpileQuery.list.Response>()),
        op.shareReplay(1)
    );

    public readonly defaultMuckpile$ = this.muckpiles$.pipe(
        op.map((v) => v?.[0] ?? null)
    );

    public readonly defaultMuckpileId$ = this.muckpiles$.pipe(
        op.map((v) => v?.[0] ?? null)
    );

    public readonly defaultDevice$ = this.devices$.pipe(
        op.map((v) => v?.[0] ?? null)
    );

    public readonly defaultDeviceUuid$ = this.devices$.pipe(
        op.map((v) => v?.[0]?.uuid ?? "")
    );

    public readonly clients$ = this.trigger$.pipe(
        switchMap(() => this.webClient.model.client.search$({})),
        shareReplay(1)
    );

    public readonly client$ = this.clients$.pipe(
        op.map(clients => clients.find(client => client.id === this.user.clientId))
    );

    public readonly muckpileId$: Observable<number | null> = this.defaultMuckpile$.pipe(
        map((muckpile) => muckpile?.id ?? null)
    );
    public readonly deviceUuid$: OverrideSubject<string> = new OverrideSubject(this.defaultDeviceUuid$);
    public readonly device$ = combineLatest([
        this.devices$,
        this.deviceUuid$
    ]).pipe(
        op.map(([devices, uuid]) => devices.find(d => d.uuid === uuid) ?? null),
        op.tap(device => {
            if (device && !this.siteId$.value) this.siteId$.next(device.siteId);
        })
    );
    public readonly muckpile$ = new OverrideSubject(this.defaultMuckpile$);
    public readonly currentInterval = new BehaviorSubject(null);
    public readonly begin$ = new BehaviorSubject(new DateTime(storageLocal.get('frag-query-begin') ?? (DateTime.now() - (60 * 60 * 1000))));
    public readonly end$ = new BehaviorSubject(new DateTime(storageLocal.get('frag-query-end') ?? DateTime.now()));
    public readonly customShiftDate$ = new BehaviorSubject(new DateTime(DateTime.now()));
    public readonly weightType$ = new BehaviorSubject<'area' | 'volume'>(storageLocal.get('frag-query-weight-type') ?? 'volume');
    public readonly chartType$ = new BehaviorSubject<'line' | 'series'>(storageLocal.get('frag-query-chart-type') ?? 'line');
    public readonly graph$ = new BehaviorSubject<'imported' | 'comparison for both' | 'tagged'>('comparison for both');
    public readonly intervalsLength = new BehaviorSubject<number>(0);
    public readonly addDataAt = new BehaviorSubject<string>(null);
    public readonly filterIntervalLength = new BehaviorSubject<number>(0);
    public readonly totalIntervalLength = new BehaviorSubject<number>(0);
    public readonly currentData = new BehaviorSubject<chart.line.Series[]>(null);
    public readonly taggedMuckpileId$ = new BehaviorSubject(null);
    public readonly selectedTimelineChunk$ = new BehaviorSubject<number>(0);
    public readonly chunkSizeSelectedByUser$ = new BehaviorSubject<boolean>(false);

    public readonly particleSizeUnit$ = this.user$.pipe(
        op.map(user => user.particleUnit),
        op.map(unit => this.unitAbbreviations[unit]),
    );

    public readonly particleSizeUnitSystem$ = this.user$.pipe(
        op.map(user => user.particleUnit),
        op.map(unit => this.unitSystem[unit]),
    );


    public readonly QuantityUnitSystem$ = this.user$.pipe(
        op.map(user => user.quantityUnit),
        op.map(unit => this.QuantitySystems[unit]),
    );


    public readonly QuantityTitle$ = combineLatest([this.user$, this.weightType$])
        .pipe(
            op.map(([user, weightType]) => {
                if (weightType === 'area')
                    return `Area ${user.quantityUnit === 'meter' ? '(m²)' : '(yard²)'}`;
                return `Volume ${user.quantityUnit === 'meter' ? '(m³)' : '(yard³)'}`;

            })
        );

    public readonly AreaAbbr$ = combineLatest([this.user$])
        .pipe(
            op.map(([user]) => {
                return `${user.quantityUnit === 'meter' ? 'm²' : 'yard²'}`;

            })
        );

    public readonly payloadUnitAbbreviation$ = this.QuantityUnitSystem$.pipe(
        op.map(unitSystem => {
            if (unitSystem == 'metricUnit') {
                return measurement.abbreviation('metricton');
            } else {
                return measurement.abbreviation('shortton');
            }
        })
    )

    public readonly shifts$: Observable<Shift[]> = this.device$.pipe(
        filter(device => !!device),
        op.switchMap(device => {
            return this.webClient.model.shift.search$({
                where: {
                    deviceId: device.id
                },
                sort: {
                    id: '+'
                }
            })
        })
    );

    public readonly defaultshift$ = this.shifts$.pipe(
        op.map((v) => v?.[0] ?? null)
    );

    public readonly shift$ = new OverrideSubject(this.defaultshift$);

    public readonly durationOptions = this.shifts$.pipe(
        op.map(shifts => {
            if (shifts.length > 0) {
                return durationOptions;
            } else {
                return durationOptions.filter(option => option.type !== 'shifts')
            }
        })
    )

    public defaultDurationOption = this.durationOptions.pipe(
        op.map(options => {
            if (options.includes(durationOptionMap.get(storageLocal.get('frag-query-duration')))) {
                return durationOptionMap.get(storageLocal.get('frag-query-duration')).key
            } else {
                return '1hour'
            }
        })
    );

    private readonly muckpileDetailsBody$ = combineLatest([
        this.muckpile$.pipe(op.filter(v => !!v)),
        this.weightType$
    ]).pipe(
        op.debounceTime(100),
        op.map(([muckpile, weightType]) => {
            const body: muckpileQuery.details.Body = {
                email: this.user.email,
                authToken: this.user.authToken,
                id: muckpile.id,
                weightType
            };
            return body;
        }),
        op.filter(body => !!body)
    );

    public readonly muckpileApiUrl$ = this.muckpileDetailsBody$.pipe(
        op.debounceTime(0),
        map((body) => {
            const modifiedBody = {
                apiKey: this.user.apiToken,
                id: body.id,
                weightType: body.weightType
            };

            return this.webClient.restUrl({
                route: 'muckpile-query/details',
                method: 'get',
                body: modifiedBody,
            });
        })
    );

    public readonly muckpileData$ = this.muckpile$.pipe(
        op.switchMap((muckpile) => {
            if (muckpile) {
                return this.weightType$.pipe(
                    op.switchMap((weightType) => {
                        const body: muckpileQuery.details.Body = {
                            email: this.user.email,
                            authToken: this.user.authToken,
                            id: muckpile.id,
                            weightType,
                        };
                        return this.webClient
                            .rest$({
                                route: '/muckpile-query/details',
                                method: 'get',
                                body,
                            })
                            .pipe(op.startWith(undefined));
                    })
                );
            } else {
                return of(null);
            }
        }),
        op.traverse(r =>
            r.toRecord<muckpileQuery.details.Response>()),
    );


    public readonly duration$ = new OverrideSubject(this.defaultDurationOption);

    public readonly timeRange$ = this.device$.pipe(
        filter(device => !!device),
        op.switchMap(device => {
            if (device) {
                return this.duration$.pipe(
                    map((key) => durationOptionMap.get(key)),
                    op.switchMap(durOpt => {
                        if (durOpt.type === 'custom') {
                            return combineLatest([this.begin$, this.end$]).pipe(
                                map(([begin, end]) => ({ begin, end }))
                            );
                        } else if (durOpt.type === 'shifts') {
                            return this.durationOptions.pipe(
                                op.switchMap(options => {
                                    if (options.includes(durationOptionMap.get('shifts'))) {
                                        return this.shift$.pipe(
                                            op.switchMap(shift => {
                                                return this.customShiftDate$.pipe(
                                                    map(customDate => {
                                                        let begin = new DateTime(customDate.setHours(
                                                            new DateTime(shift.begin * 1000).getHours(),
                                                            new DateTime(shift.begin * 1000).getMinutes(),
                                                        ));
                                                        let end = new DateTime(customDate.setHours(
                                                            new DateTime(shift.end * 1000).getHours(),
                                                            new DateTime(shift.end * 1000).getMinutes(),
                                                        ));
                                                        if (end < begin) {
                                                            end = new DateTime(end.getTime() + 24 * 60 * 60 * 1000);
                                                        }
                                                        return { begin, end };
                                                    })
                                                );
                                            })
                                        );
                                    } else {
                                        this.duration$.next('custom');
                                        return combineLatest([this.begin$, this.end$]).pipe(
                                            op.map(([begin, end]) => ({ begin, end }))
                                        );
                                    }
                                })
                            );
                        } else if (durOpt.type === 'week') {
                            return combineLatest([this.begin$, this.end$]).pipe(
                              map(([begin, end]) => {                                                                                          
                                end = new DateTime(end.getTime() + 24 * 60 * 60 * 1000 - 1000);                                                                                                                                                                               
                                return { begin, end };
                              })
                            );
                          }
                          
                        const step = durOpt.type;
                        return this.liveTimeRange(step);
                    })
                );
            } else {
                return null;
            }
        })
    ).pipe(op.tap(t => {
        if (this.chunkSizeSelectedByUser$.value === false && t && t.begin && t.end) {
            const diffMs = t.end.getTime() - t.begin.getTime() + 1000;

            const twelveHours = 12 * 60 * 60 * 1000;
            const oneDay = 24 * 60 * 60 * 1000;
            const oneWeek = 7 * 24 * 60 * 60 * 1000;

            if (diffMs >= oneWeek) {
                this.selectedTimelineChunk$.next(TimeRange.OneDay);
            } else if (diffMs >= oneDay) {
                this.selectedTimelineChunk$.next(TimeRange.ThreeHours);
            } else if (diffMs >= twelveHours) {
                this.selectedTimelineChunk$.next(TimeRange.ThirtyMinutes);
            }
        }
    }),
        shareReplay(1)
    );

    //===================== export data variables =====================//
    public readonly apiToken$ = new BehaviorSubject<string>(this.user.apiToken);
    public readonly tokenName$ = new BehaviorSubject<string>(this.user.tokenName);

    public exportReport = false;

    public generateSiteIntegrationToken = (siteId: number) => {
        const body = {
            email: this.user.email,
            authToken: this.user.authToken,
            siteId: siteId
        };

        this.trigger$
            .pipe(
                op.switchMap(() =>
                    this.webClient.rest$({
                        route: `site/generate-token`,
                        method: 'get',
                        body,
                    })
                )
            )
            .subscribe({
                next: (data) => {
                    this.trigger$.next();
                },
                error: (err) => {
                    console.error('Error generating site token:', err);
                },
            });
    };

    //=================================================================//

    public readonly percents$ = new BehaviorSubject(
        new Map(
            storageLocal
                .get<[number, string][]>('frag-query-percents')
                ?.filter(([p]) => percentageDefaultColors.has(p))
                .map(([p, c]) => [
                    p,
                    Color.html(c) ?? percentageDefaultColors.get(p),
                ]) ?? [10, 20, 50, 80].map((p) => [p, percentageDefaultColors.get(p)])
        )
    );

    public readonly newPercents$ = new BehaviorSubject(
        new Map(
            [
                ...(storageLocal
                    .get<[number, string][]>('frag-query-percents')
                    ?.filter(([p]) => percentageNewColors.has(p))
                    .map(([p, c]) => [p, c ?? percentageNewColors.get(p)] as [number, string]) ??
                    [10, 20, 50, 80, 100].map((p) => [p, percentageNewColors.get(p)]))
            ]
        )
    );

    // some dxx like 50,80,100 are necessary to calculate n and b values so ensuring that they are always there in query.
    public readonly nbValuesDxxPercents$ = this.newPercents$.pipe(
        map((newPercentsMap) => {
            const dependentPercentsMap = new Map(newPercentsMap);

            // Ensure 50, 80, and 100 are always included
            [50, 80, 100].forEach((percent) => {
                if (!dependentPercentsMap.has(percent)) {
                    dependentPercentsMap.set(percent, percentageNewColors.get(percent)!);
                }
            });

            const sortedMap = new Map([...dependentPercentsMap.entries()].sort((a, b) => a[0] - b[0]));
            return sortedMap;
        })
    );

    public readonly customSizes$ = this.device$.pipe(
        filter(device => !!device),
        map(device => {
            if (device.timelineCustomSizes) {
                const customSizes = JSON.parse(device.timelineCustomSizes) as TimelineCustomSizes[];
                let customSizeMap = new Map<number, TimelineCustomSizes>;
                if (customSizes.length) {
                    customSizes.forEach(customSize => {
                        customSizeMap.set(customSize.size, customSize);
                    })
                }
                return customSizeMap;
            } else {
                return null;
            }
        })
    );


    public readonly timelineRaw$ =
        combineLatest([
            this.device$.pipe(op.filter(v => !!v)),
            this.timeRange$,
            this.weightType$])
            .pipe(
                op.debounceTime(0),
                filter(device => !!device),
                op.switchMap(([device, timeRange, weightType]) => {
                    const body: fragQuery.timeline.raw.Body = {
                        email: this.user.email,
                        authToken: this.user.authToken,
                        deviceId: device.id,
                        begin: timeRange.begin,
                        end: timeRange.end,
                        weightType,
                        chunkSize: this.selectedTimelineChunk$.value
                    };
                    return this.webClient.rest$({
                        route: 'frag-query/timeline/raw',
                        method: 'get',
                        body,
                    }).pipe(op.startWith(undefined));
                }),
                op.traverse(r =>
                    r.toRecord<fragQuery.timeline.quantity.Response>()),
                op.traverse(r => ({
                    begin: new DateTime(r.begin),
                    end: new DateTime(r.end),
                    intervals: fixDates(r.intervals,this.user.timezone),
                    tags: fixTags(r.tags),
                })),
                op.shareReplay(1));

    public readonly addOnTimelineQuantity$ = combineLatest([
        this.device$.pipe(op.filter((v) => !!v)),
        this.timeRange$,
        this.weightType$,
        this.addDataAt,
        this.selectedTimelineChunk$
    ]).pipe(
        op.debounceTime(0),
        filter(device => !!device),
        op.switchMap(([device, timeRange, weightType, addDataAt, chunkSize]) => {
            let newTimeRange;
            const begin = timeRange.begin.valueOf();
            const end = timeRange.end.valueOf();
            const newduration = (end - begin) * 0.2;
            if (addDataAt !== null) {
                if (addDataAt === 'begin') {

                    newTimeRange = {
                        begin: new DateTime((begin - newduration)),
                        end: new DateTime(begin)
                    }
                    timeRange.begin = new DateTime((begin - newduration)),
                        timeRange.end = new DateTime(end)
                }
                if (addDataAt === 'end') {
                    newTimeRange = {
                        begin: new DateTime(end),
                        end: new DateTime(end + newduration)
                    }
                    timeRange.begin = new DateTime(begin);
                    timeRange.end = new DateTime(end + newduration)
                }
            }
            if (newTimeRange) {
                const body: fragQuery.timeline.quantity.Body = {
                    authToken: this.user.authToken,
                    email: this.user.email,
                    deviceId: device.id,
                    begin: newTimeRange.begin,
                    end: newTimeRange.end,
                    weightType,
                    chunkSize
                };

                return this.webClient
                    .rest$({
                        route: 'frag-query/timeline/quantity',
                        method: 'get',
                        body,
                    })
                    .pipe(op.startWith(undefined));
            } else {
                return of(null);
            }
        }),
        op.filter(res => res !== undefined),
        op.traverse((r) => r.toRecord<fragQuery.timeline.quantity.Response>()),
        op.traverse((r) => ({
            begin: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.begin) : new DateTime(toZonedTime(r.begin,getUserIANATimezone(this.user.timezone))),
            end: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.end) : new DateTime(toZonedTime(r.end,getUserIANATimezone(this.user.timezone))),
            intervals: fixDates(r.intervals,this.user.timezone),
            tags: fixTags(r.tags),
        })),
        op.shareReplay(1)
    );

    public readonly timelineQuantity$ = combineLatest([
        this.device$.pipe(op.filter((v) => !!v)),
        this.timeRange$,
        this.weightType$,
        this.selectedTimelineChunk$
    ]).pipe(
        op.debounceTime(0),
        op.switchMap(([device, timeRange, weightType, chunkSize]) => {
            const body: fragQuery.timeline.quantity.Body = {
                email: this.user.email,
                authToken: this.user.authToken,
                deviceId: device.id,
                begin: timeRange.begin,
                end: timeRange.end,
                weightType,
                chunkSize
            };
            return this.webClient
                .rest$({
                    route: 'frag-query/timeline/quantity',
                    method: 'get',
                    body,
                })
                .pipe(op.startWith(undefined));
        }),
        op.traverse((r) => r.toRecord<fragQuery.timeline.quantity.Response>()),
        op.traverse((r) => ({
            begin: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.begin)  : new DateTime(toZonedTime(r.begin,getUserIANATimezone(this.user.timezone))),
            end: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.end)  : new DateTime(toZonedTime(r.end,getUserIANATimezone(this.user.timezone))),
            intervals: fixDates(r.intervals,this.user.timezone),
            tags: fixTags(r.tags),
        })),
        op.shareReplay(1)
    );

    private readonly _percents$ = this.nbValuesDxxPercents$.pipe(
        op.map((v) => [0,...v.keys()]),
    );

    private readonly addOnTimelineSieveSizesAtPercentsBody$ =
        combineLatest([
            this.device$.pipe(op.filter(v => !!v)),
            this.timeRange$,
            this.weightType$,
            this._percents$,
            this.addDataAt
        ])
            .pipe(
                op.map(([device, timeRange, weightType, percents, addDataAt]) => {
                    if (!percents.includes(0))
                        percents = [0, ...percents];
                    let newTimeRange;
                    const begin = timeRange.begin.valueOf();
                    const end = timeRange.end.valueOf();
                    const newduration = (end - begin) * 0.2;
                    if (addDataAt !== null) {
                        if (addDataAt === 'begin') {

                            newTimeRange = {
                                begin: new DateTime((begin - newduration)),
                                end: new DateTime(begin)
                            }
                            timeRange.begin = new DateTime((begin - newduration)),
                                timeRange.end = new DateTime(end)
                        }
                        if (addDataAt === 'end') {
                            newTimeRange = {
                                begin: new DateTime(end),
                                end: new DateTime(end + newduration)
                            }
                            timeRange.begin = new DateTime(begin);
                            timeRange.end = new DateTime(end + newduration)
                        }
                        const body: fragQuery.timeline.sieveSizesAtPercents.Body = {
                            authToken: this.user.authToken,
                            email: this.user.email,
                            deviceId: device.id,
                            begin: newTimeRange.begin,
                            end: newTimeRange.end,
                            weightType: weightType,
                            percents: percents,
                            chunkSize: this.selectedTimelineChunk$.value
                        };
                        return body;
                    }
                    return null;
                }));

    public readonly addOnTimelineSieveSizesAtPercents$ = this.addOnTimelineSieveSizesAtPercentsBody$
        .pipe(
            op.switchMap((body) => {
                if (body) {
                    return this.webClient.rest$({
                        route: 'frag-query/timeline/sieve-sizes-at-percents',
                        method: 'get',
                        body,
                    }).pipe(op.startWith(undefined));
                } else {
                    return of(null);
                }
            }),
            op.traverse(r =>
                r.toRecord<fragQuery.timeline.sieveSizesAtPercents.Response>()),
            op.traverse(r => ({
                deviceId: r.deviceId,
                begin: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.begin) : new DateTime(toZonedTime(r.begin,getUserIANATimezone(this.user.timezone))),
                end: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.end) : new DateTime(toZonedTime(r.end,getUserIANATimezone(this.user.timezone))),
                percents: r.percents,
                intervals: fixDates(r.intervals,this.user.timezone),
                tags: fixTags(r.tags),
                summary: r.summary,
                totalIntervalCount: r.totalIntervalCount
            })),
            op.tap(r => {
                if (r && r.intervals && r.intervals.length > 0) {
                    snapshotManager.setIndex(0)
                }
            }),
            op.shareReplay(1));

    private readonly timelineSieveSizesAtPercentsBody$ =
        combineLatest([
            this.device$.pipe(op.filter(v => !!v)),
            this.timeRange$,
            this.weightType$,
            this._percents$,
            this.selectedTimelineChunk$
        ])
            .pipe(
                op.debounceTime(100),
                op.map(([device, timeRange, weightType, percents, timeLineChunk]) => {
                    const beginDate = new Date(timeRange.begin);
                    const endDate = new Date(timeRange.end);
                    const durationMonths = (endDate.getFullYear() - beginDate.getFullYear()) * 12 + endDate.getMonth() - beginDate.getMonth();

                    if (durationMonths >= 4) {
                        this.snackBar.open('Please select a duration of up to 4 months.', 'OK', {
                            duration: 5000,
                            panelClass: ['snackbar-success']
                        });
                        return null;
                    }
                    if (!percents.includes(0))
                        percents = [0, ...percents];

                    const body: fragQuery.timeline.sieveSizesAtPercents.Body = {
                        email: this.user.email,
                        authToken: this.user.authToken,
                        deviceId: device.id,
                        begin: timeRange.begin,
                        end: timeRange.end,
                        weightType: weightType,
                        percents: percents,
                        chunkSize : timeLineChunk
                    };
                    return body;
                }),
                op.filter(body => !!body)
            );

    public readonly timelineSieveSizesAtPercents$ = this.timelineSieveSizesAtPercentsBody$
        .pipe(
            op.switchMap((body) => {
                return this.webClient.rest$({
                    route: 'frag-query/timeline/sieve-sizes-at-percents',
                    method: 'get',
                    body,
                }).pipe(op.startWith(undefined),
            op.tap(()=> snapshotManager.setLoading(true)));
            }),
            op.traverse(r =>
                r.toRecord<fragQuery.timeline.sieveSizesAtPercents.Response>()),
            op.traverse(r => ({
                deviceId: r.deviceId,
                begin: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.begin) : new DateTime(toZonedTime(r.begin,getUserIANATimezone(this.user.timezone))),
                end: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.end) : new DateTime(toZonedTime(r.end, getUserIANATimezone(this.user.timezone))),
                percents: r.percents,
                intervals: fixDates(r.intervals, this.user.timezone),
                tags: fixTags(r.tags),
                summary: r.summary,
                efficiency:r.efficiency,
                offBeltDuration: r.offBeltDuration,
                blackBeltDuration: r.blackBeltDuration,
                totalTime: r.totalTime,
                totalIntervalCount: r.totalIntervalCount,
            })),
            op.tap(r => {
                this.addDataAt.next(null);
                storageLocal.set('scrollLeft', 0);
                if (r && r.intervals && r.intervals.length > 0) {
                    snapshotManager.setIndex(0)
                    this.intervalsLength.next(r.intervals.length);
                    this.filterIntervalLength.next((r.end.valueOf() - r.begin.valueOf()))
                    this.totalIntervalLength.next((r.end.valueOf() - r.begin.valueOf()))
                }
            }),
            op.shareReplay(1));

    private readonly exportTimelineSieveSizesAtPercentsBody$ = combineLatest([
        this.device$.pipe(op.filter(v => !!v)),
        this.timeRange$,
        this.weightType$,
        this._percents$,
    ]).pipe(
        debounceTime(100),
        map(([device, timeRange, weightType, percents]) => {
            if (!percents.includes(0)) percents = [0, ...percents];
            const begin = timeRange.begin;
            const end = timeRange.end;
            const twoWeeksInMillis = 1000 * 60 * 60 * 24 * 7 * 2;
            const durationInMillis = Math.abs(end.valueOf() - begin.valueOf());
            if (durationInMillis > twoWeeksInMillis) {
                this.snackBar.open('Please select a duration of up to two weeks.', 'OK', {
                    duration: 5000,
                    panelClass: ['snackbar-success']
                });
                return null;
            }


            const body: fragQuery.timeline.sieveSizesAtPercents.Body = {
                email: this.user.email,
                authToken: this.user.authToken,
                deviceId: device.id,
                begin: timeRange.begin,
                end: timeRange.end,
                weightType: weightType,
                percents: percents,
                chunkSize: 0 // No chunk size for export
            };

            return body;
        }),
        op.filter(body => !!body)
    );


    public readonly timelineSieveSizesAtPercentsUrl$ = this.timelineSieveSizesAtPercentsBody$.pipe(
        op.debounceTime(0),
        map((body) => {
            const modifiedBody = {
                apiKey: this.user.apiToken,
                deviceId: body.deviceId,
                begin: body.begin,
                end: body.end,
                weightType: body.weightType,
                percents: body.percents,
                chunkSize: body.chunkSize
            };
            return this.webClient.restUrl({
                route: 'frag-query/timeline/sieve-sizes-at-percents',
                method: 'get',
                body: modifiedBody,
            });
        })
    ); 

    public totalWeight$ = this.timelineSieveSizesAtPercents$.pipe(
        op.map(input => (input && input.summary && input.summary.totalWeight) ? input.summary.totalWeight.toFixed(2) : null)
    );

    public totalPayload$: Observable<number> = combineLatest(
        [
            this.timelineSieveSizesAtPercents$,
            this.totalWeight$,
            this.QuantityUnitSystem$
        ]
    ).pipe(
        map(([input, totalWeight, quantityUnitSystem]) => {
            if (input && input.summary && totalWeight) {
                const totalVolume = parseFloat(totalWeight);
                const typeToUnit = this.measurementTypeToUnit.getUnitsForSystem(quantityUnitSystem);
                let density;
                this.device$.subscribe(device => {
                    density = device.density
                });
                const densityAfterUnitConversion = measurement.convertFromBaseUnits(measurement.convertToBaseUnits(density, 'kilogramPerMeter3'), typeToUnit.largeDensity);
                const totalPayload = totalVolume * densityAfterUnitConversion;
                return parseFloat(totalPayload.toFixed(2));
            }
            return null;
        })
    )

    public actualPayload$: Observable<number> = combineLatest([
        this.timelineSieveSizesAtPercents$,
        this.QuantityUnitSystem$
    ]).pipe(
        map(([input, quantityUnitSystem]) => {
            if (input && input.summary) {
                let actualPayload = 0;
                const typeToUnit = this.measurementTypeToUnit.getUnitsForSystem(quantityUnitSystem);
                input.tags.forEach((tag) => {
                    if (tag.truckMaterialDensity && tag.truckLoadCount) {
                        const densityAfterUnitConversion = measurement.convertFromBaseUnits(
                            measurement.convertToBaseUnits(tag.truckMaterialDensity, 'kilogramPerMeter3'),
                            typeToUnit.largeDensity
                        );
                        const payload = tag.truckLoadCount * densityAfterUnitConversion;
                        actualPayload += parseFloat(payload.toFixed(2));
                    }
                });
                return parseFloat(actualPayload.toFixed(2));
            }
            return null;
        })
    );

    private readonly liveDataIntervalTagTrigger$ = new Subject<void>();
    public readonly liveDataIntervalTag$ = this.liveDataIntervalTagTrigger$.pipe(
        op.startWith(0),
        op.switchMap(() => this.device$),
        op.switchMap((device) =>
            this.webClient.model.dataIntervalTag.getLive$(device.id)
        ),
        op.shareReplay(1)
    );

    public init() {
        // Only on the first devices$ emission: set initial deviceUuid from storage or first in list
        this.devices$.pipe(take(1)).subscribe(devices => {
            if (devices && devices.length > 0) {
                const stored = storageLocal.get('frag-query-device-uuid');
                const found = devices.find(d => d.uuid === String(stored));
                const toUuid = found ? found.uuid : devices[0].uuid;
                this.deviceUuid$.next(toUuid);
            } else {
                this.deviceUuid$.next(null);
            }
        });

        this.device$.subscribe(device => {
            if (device && device.uuid) {
                storageLocal.set('frag-query-device-uuid', device.uuid);
            } else {
                storageLocal.delete('frag-query-device-uuid');
            }
        });

        this.duration$.subscribe((v) => storageLocal.set('frag-query-duration', v));

        this.begin$.subscribe((v) => storageLocal.set('frag-query-begin', v));
        this.end$.subscribe((v) => storageLocal.set('frag-query-end', v));
        this.weightType$.subscribe((v) =>
            storageLocal.set('frag-query-weight-type', v)
        );
        this.chartType$.subscribe((v) =>
            storageLocal.set('frag-query-chart-type', v)
        );
        this.newPercents$.subscribe((v) =>
            storageLocal.set('frag-query-percents', [...v])
        );
    }
    public async setLiveTag(tag: DataIntervalTag) {
        const response = await this.webClient.model.dataIntervalTag.setLiveTag(tag);
        this.liveDataIntervalTagTrigger$.next();
        return response
    }

    public async setDataIntervalTag(tag: DataIntervalTag) {
        const response = await this.webClient.model.dataIntervalTag.save(tag);
        this.liveDataIntervalTagTrigger$.next();
        return response
    }

    public async openLiveTagDialog(newTag?: boolean, showTimes?:boolean) {
        const device = await firstValueFrom(this.device$);
        let tag: DataIntervalTag;
        if (!newTag) {
            tag = await firstValueFrom(this.liveDataIntervalTag$);
        }
        tag = tag?.clone() ?? new DataIntervalTag();
        tag.deviceId = device.id;
        tag.clientId = this.user.clientId
        tag.siteId = this.user.siteId
        if (
            await this.dialog.open(DataIntervalTagEditDialog, {
                record: tag,
                showTimes: showTimes ?? false,
            })
        ) {
            if (showTimes)
                return await this.dialog.await(this.setDataIntervalTag(tag));
            else
                return await this.dialog.await(this.setLiveTag(tag));
        } else return false
    }

    public async exportTimelineSieveSizesAtPercents() {
        const body = await firstValueFrom(this.exportTimelineSieveSizesAtPercentsBody$);
        const reponse = await this.webClient.rest({
            route: 'frag-query/timeline/sieve-sizes-at-percents',
            method: 'get',
            body,
        });
        const r = reponse.toRecord<fragQuery.timeline.sieveSizesAtPercents.Response>();
        return {
            deviceId: r.deviceId,
            begin: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.begin) : new DateTime(toZonedTime(r.begin,getUserIANATimezone(this.user.timezone))),
            end: (!this.user.timezone || this.user.timezone === 'Default') ? new DateTime(r.end) : new DateTime(toZonedTime(r.end, getUserIANATimezone(this.user.timezone))),
            percents: r.percents,
            intervals: fixDates(r.intervals,this.user.timezone),
            tags: fixTags(r.tags),
            summary: r.summary,
            totalIntervalCount: r.totalIntervalCount,
        };
    }

  public async removeDataIntervals(dataIntervals: number[]) {
      const device = await firstValueFrom(this.device$);
      const deviceId = device.id;

    try {
      const response = await this.webClient.rest({
        route: 'data-interval/remove',
        method: 'post',
        body: {
          email: this.user.email,
          authToken: this.user.authToken,
          deviceId,
          dataIntervalIds: dataIntervals,
        },
      });
      const responceRecord = response.toRecord<RemoveDataInterval.Response>();

      this.snackBar.open(`${responceRecord.success} interval(s) removed successfully!`, 'OK', {
        duration: 5000,
        panelClass: ['snackbar-success'],
      });

      if (responceRecord.success){
          this.trigger$.next();
      }

      return responceRecord;
    } catch (error) {
      console.error('Error deleting data intervals:', error);
      
      this.snackBar.open(
        'Failed to delete interval(s). Please try again.',
        'OK',
        {
          duration: 5000, 
          panelClass: ['snackbar-error'],
        }
      );

            return null;
        }
    }

    public async taggingMuckpile(dataIntervals: number[], muckpileId: number) {
        try {
            const response = await this.webClient.rest({
                route: '/muckpile/tag',
                method: 'post',
                body: {
                    email: this.user.email,
                    authToken: this.user.authToken,
                    muckpileId,
                    dataIntervalIds: dataIntervals,
                },
            });
            const responce = response.toRecord<TaggingMuckpile.Response>();

            this.snackBar.open(`Muckpile's tag successfully!`, 'OK', {
                duration: 5000,
                panelClass: ['snackbar-success'],
            });

            if (responce.success) {
                this.trigger$.next();
            }

            return responce;
        } catch (error) {
            console.error('Error deleting tagging muckpiles:', error);

            this.snackBar.open(
                'Failed to tag muckpiles. Please try again.',
                'OK',
                {
                    duration: 5000,
                    panelClass: ['snackbar-error'],
                }
            );

            return null;
        }
    }

    public async deleteMuckpile(muckpileId: number[]) {
        try {
            const response = await this.webClient.rest({
                route: '/muckpile/delete',
                method: 'delete',
                body: {
                    email: this.user.email,
                    authToken: this.user.authToken,
                    muckpileId,
                },
            });
            const responce = response.toRecord<TaggingMuckpile.Response>();

            this.snackBar.open(`Muckpile's delete successfully!`, 'OK', {
                duration: 5000,
                panelClass: ['snackbar-success'],
            });

            if (responce.success) {
                this.trigger$.next();
            }

            return responce;
        } catch (error) {
            console.error('Error deleting muckpiles:', error);

            this.snackBar.open(
                'Failed to delete muckpiles. Please try again.',
                'OK',
                {
                    duration: 5000,
                    panelClass: ['snackbar-error'],
                }
            );

            return null;
        }
    }

    public async setMuckpileStatus(muckpileId: number, isActive: boolean) {
        try {
            const response = await this.webClient.rest({
                route: '/muckpile/status',
                method: 'post',
                body: {
                    email: this.user.email,
                    authToken: this.user.authToken,
                    muckpileId,
                    isActive,
                },
            });
            
            const responseData = response.toRecord<MuckpileStatusUpdate.Response>();
    
            this.snackBar.open(`Muckpile status updated to ${isActive}!`, 'OK', {
                duration: 5000,
                panelClass: ['snackbar-success'],
            });
    
            if (responseData.success) {
                this.trigger$.next();
            }
    
            return responseData;
        } catch (error) {
            console.error('Error updating muckpile status:', error);
    
            this.snackBar.open(
                'Failed to update muckpile status. Please try again.',
                'OK',
                {
                    duration: 5000,
                    panelClass: ['snackbar-error'],
                }
            );
    
            return null;
        }
    }

        public readonly taggedMuckpileData$ = combineLatest([
        this.taggedMuckpileId$.pipe(op.filter(Boolean)),
        this.weightType$.pipe(op.filter(Boolean))
    ]).pipe(
        op.switchMap(([muckpileId, weightType]) => {
            const body = {
                email: this.user.email,
                authToken: this.user.authToken,
                id: muckpileId,
                weightType
            };
            return this.webClient.rest$({
                route: '/muckpile-query/details',
                method: 'get',
                body
            }).pipe(op.startWith(undefined));
        }),
        op.traverse(r => r.toRecord<muckpileQuery.details.Response>())
    );

    public readonly exportTimelineSieveSizesAtPercents$ =
        this.exportTimelineSieveSizesAtPercentsBody$.pipe(
            op.switchMap((body) => {
                return this.webClient
                    .rest$({
                        route: 'frag-query/timeline/sieve-sizes-at-percents',
                        method: 'get',
                        body,
                    })
                    .pipe(op.startWith(undefined));
            }),
            op.traverse((r) =>
                r.toRecord<fragQuery.timeline.sieveSizesAtPercents.Response>()
            ),
            op.traverse((r) => ({
                deviceId: r.deviceId,
                begin:
                    !this.user.timezone || this.user.timezone === 'Default'
                        ? new DateTime(r.begin)
                        : new DateTime(toZonedTime(r.begin, getUserIANATimezone(this.user.timezone))),
                end:
                    !this.user.timezone || this.user.timezone === 'Default'
                        ? new DateTime(r.end)
                        : new DateTime(toZonedTime(r.end, getUserIANATimezone(this.user.timezone))),
                percents: r.percents,
                intervals: fixDates(
                    r.intervals,
                    this.user.timezone
                ),
                tags: fixTags(r.tags),
                summary: r.summary,
                totalIntervalCount: r.totalIntervalCount
            })),
            op.shareReplay(1)
        );

    public getTimelineSieveSizesForDevice(
        device: Device,
        requestedPercents?: number[],
    ): Observable<fragQuery.timeline.sieveSizesAtPercents.Response | null | undefined> {
        const percents$ = requestedPercents ? of(requestedPercents) : this._percents$;
        return defer(() =>
            combineLatest([
                this.timeRange$,
                this.weightType$,
                percents$,
                of(device)
            ]).pipe(
                debounceTime(100),
                map(([timeRange, weightType, percents, currentDevice]) => {
                    const beginDate = new Date(timeRange.begin);
                    const endDate = new Date(timeRange.end);
                    const durationMonths =
                        (endDate.getFullYear() - beginDate.getFullYear()) * 12 +
                        endDate.getMonth() - beginDate.getMonth();

                    if (durationMonths >= 4) {
                        this.snackBar.open('Please select a duration of up to 4 months.', 'OK', {
                            duration: 5000,
                            panelClass: ['snackbar-success']
                        });
                        return null;
                    }

                    percents = [...percents];
                    if (!percents.includes(0)) {
                        percents = [0, ...percents];
                    }

                    const body: fragQuery.timeline.sieveSizesAtPercents.Body = {
                        email: this.user.email,
                        authToken: this.user.authToken,
                        deviceId: currentDevice.id,
                        begin: timeRange.begin,
                        end: timeRange.end,
                        weightType: weightType,
                        percents: percents,
                        chunkSize: 0
                    };
                    return body;
                }),
                switchMap(body => {
                    if (!body) {
                        return of(null);
                    }
                    return concat(
                        of(undefined),
                        this.webClient.rest({
                            route: 'frag-query/timeline/sieve-sizes-at-percents',
                            method: 'get',
                            body,
                        }),
                    );
                }),
                op.traverse(r => r.toRecord<fragQuery.timeline.sieveSizesAtPercents.Response>()),
                op.traverse(r => ({
                    deviceId: r.deviceId,
                    begin: (!this.user.timezone || this.user.timezone === 'Default')
                        ? new DateTime(r.begin)
                        : new DateTime(toZonedTime(r.begin, getUserIANATimezone(this.user.timezone))),
                    end: (!this.user.timezone || this.user.timezone === 'Default')
                        ? new DateTime(r.end)
                        : new DateTime(toZonedTime(r.end, getUserIANATimezone(this.user.timezone))),
                    percents: r.percents,
                    intervals: fixDates(r.intervals, this.user.timezone),
                    tags: fixTags(r.tags),
                    summary: r.summary,
                    totalIntervalCount: r.totalIntervalCount
                }))
            )
        );
    }

    public getMaterialOverTimeForDevice(
        device: Device
    ): Observable<fragQuery.timeline.materialOverTime.Response | null | undefined> {
        return defer(() =>
            combineLatest([
                this.timeRange$,
                this.weightType$,
            ]).pipe(
                debounceTime(100),
                switchMap(([timeRange, weightType]) => {
                    if (this.isTimelineSpanTooLong(timeRange)) {
                        this.showTimelineSpanLimitSnackbar(timeRange);
                        return of(null);
                    }
                    this.lastTimelineSpanLimitSnackbarKey = null;

                    const body: fragQuery.timeline.materialOverTime.Body = {
                        email: this.user.email,
                        authToken: this.user.authToken,
                        deviceId: device.id,
                        begin: timeRange.begin,
                        end: timeRange.end,
                        weightType,
                        chunkSize: 0,
                    };
                    return concat(
                        of(undefined),
                        this.webClient.rest({
                            route: 'frag-query/timeline/material-over-time',
                            method: 'get',
                            body,
                        }),
                    );
                }),
                op.traverse(r => r.toRecord<fragQuery.timeline.materialOverTime.Response>()),
                op.traverse(r => ({
                    deviceId: r.deviceId,
                    begin: (!this.user.timezone || this.user.timezone === 'Default')
                        ? new DateTime(r.begin)
                        : new DateTime(toZonedTime(r.begin, getUserIANATimezone(this.user.timezone))),
                    end: (!this.user.timezone || this.user.timezone === 'Default')
                        ? new DateTime(r.end)
                        : new DateTime(toZonedTime(r.end, getUserIANATimezone(this.user.timezone))),
                    intervals: fixDates(r.intervals, this.user.timezone),
                }))
            )
        );
    }

    public readonly timelineSieveSizesAtPercentsCopy$: Observable<fragQuery.timeline.sieveSizesAtPercents.Response | null | undefined> =
        this.device$.pipe(
            op.filter(v => !!v),
            op.switchMap(device => this.getTimelineSieveSizesForDevice(device)),
            op.tap(r => {
                this.addDataAt.next(null);
                storageLocal.set('scrollLeft', 0);
                if (r && r.intervals && r.intervals.length > 0) {
                    
                    this.intervalsLength.next(r.intervals.length);
                    this.filterIntervalLength.next((r.end.valueOf() - r.begin.valueOf()))
                    this.totalIntervalLength.next((r.end.valueOf() - r.begin.valueOf()))
                }
            }),
            shareReplay(1)
        );

    public accumulatedData$: Observable<fragQuery.timeline.sieveSizesAtPercents.Interval> =
        this.timelineSieveSizesAtPercentsCopy$.pipe(
            op.map((input) => getSummaryData(input))
        );

}

export function fixDates<
    T extends {
        begin: DateTime;
        end: DateTime;
    }
>(intervals: T[], timezone: string) {
    for (const interval of intervals) {
        interval.begin =
            !timezone || timezone === "Default"
                ? new DateTime(interval.begin)
                : new DateTime(toZonedTime(interval.begin, getUserIANATimezone(timezone)));
        interval.end =
            !timezone || timezone === "Default"
                ? new DateTime(interval.end)
                : new DateTime(toZonedTime(interval.end, getUserIANATimezone(timezone)));
    }
    return intervals;
}

export function fixTags(tags: DataIntervalTag.RestTypes['response']['Get'][]) {
    return tags.map((v) => {
        const tag = new DataIntervalTag();
        DataIntervalTag.meta.copyToInstance(v, tag);
        return tag;
    });
}

export namespace FragQueryService { }
